/** @jsxImportSource @emotion/react */
import { prettyFormatCurrency } from '@quickbit/backoffice-core-frontend/src/utils/formatCurrency';
import {
  List,
  ListItem,
  useTheme,
  iconMap,
  IconName,
  Typography,
  UtilityButton,
  UtilitySelect,
  Option,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAsyncFn, useMount } from 'react-use';
import { QBLoader } from 'components';
import {
  exportAffiliateTransactions,
  searchAffiliateTransactions,
  UserType,
} from '../../api';

const userTypeOptions: Option[] = [
  { value: UserType.AFFILIATE, label: 'Valuno' },
  { value: UserType.DIRECT_ONRAMP, label: 'Sigulda' },
];

interface Props {
  userId: string;
}

export const AffiliateTransactions = ({ userId }: Props) => {
  const { spacing, palette, shape } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTxId, setSelectedTxId] = useState<string | undefined>(
    () => searchParams.get('transactionId') ?? undefined
  );
  const [offsetTop, setOffsetTop] = useState<number>();
  const [selectedUserType, setSelectedUserType] = useState<Option | undefined>(
    () => {
      const userTypeParam = searchParams.get('userType');
      if (userTypeParam == 'null') {
        return undefined;
      }

      return (
        userTypeOptions.find((opt) => opt.value === userTypeParam) ??
        userTypeOptions[0]
      );
    }
  );

  const [{ value, loading }, refreshTransactions] = useAsyncFn(
    (userType?: UserType) => {
      return searchAffiliateTransactions({
        filters: { userId, user_type: userType },
      });
    }
  );

  const transactions = value
    ? Object.values(value.transactions).sort((a, b) =>
        new Date(a.createdAt) < new Date(b.createdAt) ? 1 : -1
      )
    : undefined;

  const [{ loading: exportLoading }, exportTransactionsFn] = useAsyncFn(
    async () => {
      if (userId) {
        const blob = await exportAffiliateTransactions(userId);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${userId}-affiliate-transactions.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      }
    }
  );

  useMount(() => {
    void refreshTransactions(selectedUserType?.value as UserType);
  });

  return (
    <>
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          marginBottom: spacing.xxs,
          gap: spacing.xxxs,
        }}
      >
        <Typography variant="subtitle">Export all Transactions</Typography>
        <div css={{ display: 'flex', gap: spacing.xxs }}>
          <UtilityButton
            icon="ALGO"
            text="Export Transactions"
            loading={exportLoading}
            disabled={exportLoading}
            onClick={exportTransactionsFn}
          />
          <UtilitySelect
            value={selectedUserType ?? null}
            options={userTypeOptions}
            onSelect={(selection) => {
              const option = selection as Option | null;
              setSelectedUserType(option ?? undefined);
              if (option?.value) {
                searchParams.set('userType', option.value);
              } else {
                searchParams.delete('userType');
              }
              setSearchParams(searchParams);
              void refreshTransactions(option?.value as UserType);
            }}
          />
        </div>
      </div>
      <div css={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="heading2">Transactions</Typography>
        <Typography variant="heading2">
          Total:{' '}
          {(value && prettyFormatCurrency(value.aggregatedData.total)) ?? ''}
        </Typography>
      </div>
      {loading && <QBLoader />}
      <div
        css={[
          { display: 'grid', gap: spacing.xxs, position: 'relative' },
          selectedTxId && { gridTemplateColumns: '1fr 1fr' },
        ]}
      >
        <List>
          {transactions &&
            transactions.map((transaction) => (
              <ListItem
                key={transaction.transactionId}
                icon={
                  transaction.requestedCurrency in iconMap
                    ? (transaction.requestedCurrency as IconName)
                    : undefined
                }
                title={[
                  transaction.orderRef,
                  transaction.pspTransactionId,
                  transaction.pspId,
                ].join(' | ')}
                subtitle={new Date(transaction.createdAt).toLocaleString()}
                content={`Settled ${transaction.settledAmount} ${transaction.settledCurrency}`}
                subcontent={`Requested ${transaction.requestedAmount} ${transaction.requestedCurrency}`}
                css={
                  selectedTxId === transaction.transactionId
                    ? { backgroundColor: palette.grey.x100 }
                    : undefined
                }
                onClick={(e) => {
                  e.stopPropagation();
                  const newTxId = transaction.transactionId;
                  setSelectedTxId(newTxId);
                  if (newTxId) {
                    searchParams.set('transactionId', newTxId);
                  } else {
                    searchParams.delete('transactionId');
                  }
                  setSearchParams(searchParams);
                  setOffsetTop(
                    selectedTxId === newTxId
                      ? undefined
                      : e.currentTarget.offsetTop
                  );
                }}
              />
            ))}
        </List>
        {selectedTxId && (
          <div
            css={{
              background: palette.white,
              padding: spacing.xxs,
              border: `1px solid ${palette.grey.x100}`,
              borderRadius: shape.borderRadius,
              display: 'flex',
              flexDirection: 'column',
              gap: spacing.xxs,
              position: 'absolute',
              width: '49%',
              right: 0,
              top: offsetTop ? offsetTop : 0,
            }}
          >
            <Typography variant="bodyBold">Transaction Details</Typography>
            <pre>
              {JSON.stringify(
                transactions?.find((tx) => tx.transactionId === selectedTxId)
                  ?.rawTransaction ?? '',
                null,
                2
              )}
            </pre>
          </div>
        )}
      </div>
    </>
  );
};
